import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Save, Loader2, BellRing, Copy, RefreshCw, Eye, EyeOff, KeyRound } from "lucide-react";
import { AppShell } from "@/components/app/AppShell";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import api, { API } from "@/lib/api";
import { AuditTrailCard } from "@/components/AuditTrailCard";
import { TrustedDevicesCard } from "@/components/TrustedDevicesCard";

export default function Settings() {
  const { company, user, reload } = useAuth();
  const [form, setForm] = useState({ name: "", sector: "", stage: "", description: "", website: "", currency: "USD", email_domain: "", cadence: "monthly" });
  const [saving, setSaving] = useState(false);
  const [emailEnabled, setEmailEnabled] = useState(false);
  const [reminders, setReminders] = useState(null);
  const [savingReminders, setSavingReminders] = useState(false);
  const [inbound, setInbound] = useState(null);
  const [showSecret, setShowSecret] = useState(false);
  const [inboundBusy, setInboundBusy] = useState(false);

  useEffect(() => {
    if (company) setForm({
      name: company.name || "", sector: company.sector || "", stage: company.stage || "",
      description: company.description || "", website: company.website || "", currency: company.currency || "USD",
      email_domain: company.email_domain || "", cadence: company.cadence || "monthly",
    });
  }, [company]);

  useEffect(() => {
    api.get(`/config`).then(({ data }) => setEmailEnabled(!!data.email_enabled)).catch(() => {});
    if (company) api.get(`/companies/${company.id}/reminder-settings`).then(({ data }) => setReminders(data)).catch(() => {});
    api.get(`/impact/inbound-config`).then(({ data }) => setInbound(data)).catch(() => {});
  }, [company]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try { await api.put(`/companies/${company.id}`, form); await reload(); toast.success("Company profile saved"); }
    catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const setR = (k, v) => setReminders((r) => ({ ...r, [k]: v }));
  const saveReminders = async () => {
    setSavingReminders(true);
    try {
      const { data } = await api.put(`/companies/${company.id}/reminder-settings`, reminders);
      setReminders(data);
      toast.success("Reminder settings saved");
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
    finally { setSavingReminders(false); }
  };

  const sendDeckTest = async () => {
    try { const { data } = await api.post(`/companies/${company.id}/deck-digest/send-test`); toast.success(`Test digest ready (${data.totals?.views ?? 0} views)`); }
    catch (err) { toast.error(err?.response?.data?.detail || "Could not send test"); }
  };

  const sendImpactTest = async () => {
    try { const { data } = await api.post(`/companies/${company.id}/impact-digest/send-test`); toast.success(`Test sent (avg ${data.avg}/100, ${data.count} investors)`); }
    catch (err) { toast.error(err?.response?.data?.detail || "Could not send test"); }
  };

  const sendReadinessTest = async () => {
    try { const { data } = await api.post(`/companies/${company.id}/readiness-nudge/send-test`); toast.success(`Test sent (score ${data.score}/100, ${data.band})`); }
    catch (err) { toast.error(err?.response?.data?.detail || "Could not send test"); }
  };

  const sendBriefingTest = async () => {
    try { const { data } = await api.post(`/companies/${company.id}/founder-briefing/send-test`); toast.success(`Test sent (readiness ${data.readiness}/100, ${data.deck_views} deck views, impact avg ${data.impact_avg})`); }
    catch (err) { toast.error(err?.response?.data?.detail || "Could not send test"); }
  };

  const webhookUrl = `${API}/webhooks/inbound-email`;
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  const [pwSaving, setPwSaving] = useState(false);
  const changePassword = async (e) => {
    e.preventDefault();
    if (pw.new_password !== pw.confirm) { toast.error("New passwords don't match"); return; }
    setPwSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: pw.current_password, new_password: pw.new_password });
      setPw({ current_password: "", new_password: "", confirm: "" });
      toast.success("Password updated");
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not update password"); }
    finally { setPwSaving(false); }
  };
  const copyInbound = (text, label) => { navigator.clipboard?.writeText(text); toast.success(`${label} copied`); };
  const rotateSecret = async () => {
    setInboundBusy(true);
    try { const { data } = await api.post(`/impact/inbound-config/rotate`); setInbound((s) => ({ ...s, secret: data.secret })); setShowSecret(true); toast.success("New secret generated - update your mail provider"); }
    catch (err) { toast.error(err?.response?.data?.detail || "Could not rotate secret"); }
    finally { setInboundBusy(false); }
  };
  const testInbound = async () => {
    setInboundBusy(true);
    try { const { data } = await api.post(`/impact/inbound-config/test`, { secret: inbound?.secret }); toast.success(data.message || "Webhook ready"); }
    catch (err) { toast.error(err?.response?.data?.detail || "Webhook test failed"); }
    finally { setInboundBusy(false); }
  };

  const ToggleRow = ({ label, hint, k }) => (
    <div className="flex items-start justify-between gap-4 rounded-lg border p-3">
      <div>
        <div className="text-sm font-medium text-foreground">{label}</div>
        <div className="text-xs text-muted-foreground">{hint}</div>
      </div>
      <Switch checked={!!reminders?.[k]} onCheckedChange={(v) => setR(k, v)} data-testid={`reminder-toggle-${k}`} />
    </div>
  );

  return (
    <AppShell title="Settings" subtitle="Your company profile & automation">
      <div className="max-w-2xl space-y-6">
        <Card className="rounded-xl border bg-card p-6">
          <form onSubmit={save} className="space-y-4">
            <div>
              <Label htmlFor="cname">Company name</Label>
              <Input id="cname" value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-1" data-testid="settings-name" />
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div><Label htmlFor="sector">Sector</Label><Input id="sector" value={form.sector} onChange={(e) => set("sector", e.target.value)} className="mt-1" placeholder="B2B SaaS" data-testid="settings-sector" /></div>
              <div><Label htmlFor="stage">Stage</Label><Input id="stage" value={form.stage} onChange={(e) => set("stage", e.target.value)} className="mt-1" placeholder="Seed" data-testid="settings-stage" /></div>
              <div><Label htmlFor="currency">Currency</Label><Input id="currency" value={form.currency} onChange={(e) => set("currency", e.target.value)} className="mt-1" data-testid="settings-currency" /></div>
            </div>
            <div><Label htmlFor="website">Website</Label><Input id="website" value={form.website} onChange={(e) => set("website", e.target.value)} className="mt-1" placeholder="https://" data-testid="settings-website" /></div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <div className="flex items-center gap-2">
                  <Label htmlFor="edomain">Email sending domain</Label>
                  {emailEnabled ? <Badge className="border-emerald-200 bg-emerald-50 text-emerald-700">Email live</Badge> : <Badge className="border-amber-200 bg-amber-50 text-amber-800">Simulated</Badge>}
                </div>
                <Input id="edomain" value={form.email_domain} onChange={(e) => set("email_domain", e.target.value)} className="mt-1" placeholder="yourcompany.com" data-testid="settings-email-domain" />
                <p className="mt-1 text-xs text-muted-foreground" data-testid="settings-email-sender-note">Delivery uses the app's configured email sender.</p>
              </div>
              <div>
                <Label htmlFor="cadence">Update cadence</Label>
                <Select value={form.cadence} onValueChange={(v) => set("cadence", v)}>
                  <SelectTrigger id="cadence" className="mt-1" data-testid="settings-cadence"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="quarterly">Quarterly</SelectItem>
                  </SelectContent>
                </Select>
                <p className="mt-1 text-xs text-muted-foreground">Used for reminders on your dashboard.</p>
              </div>
            </div>
            <div><Label htmlFor="desc">One-line description</Label><Textarea id="desc" value={form.description} onChange={(e) => set("description", e.target.value)} className="mt-1" data-testid="settings-description" /></div>
            <Button type="submit" disabled={saving} className="gap-2" data-testid="settings-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save profile
            </Button>
          </form>
        </Card>

        <Card className="rounded-xl border bg-card p-6" data-testid="settings-subscription-card">
          <h2 className="h-display mb-3 text-lg font-semibold text-foreground" data-testid="settings-subscription-title">Subscription</h2>
          {company?.founder_user_id === user?.id ? <>
            <p className="mb-4 text-sm text-muted-foreground" data-testid="settings-subscription-description">Review invoices, change your plan, or cancel a paid subscription. Canceling a plan does not delete your account or workspace data.</p>
            <Button asChild variant="outline" data-testid="settings-manage-billing"><Link to="/app/billing">Manage billing</Link></Button>
          </> : <p className="text-sm text-muted-foreground" data-testid="settings-billing-owner-only">Billing is managed by the workspace owner.</p>}
        </Card>

        <Card className="rounded-xl border bg-card p-6" data-testid="reminder-settings-card">
          <div className="mb-4 flex items-center gap-2">
            <BellRing className="h-5 w-5 text-primary" />
            <div>
              <h2 className="h-display text-lg font-semibold text-foreground">Reminders & nudges</h2>
              <p className="text-xs text-muted-foreground">Control the automatic nudges tenx sends (email + in-app).</p>
            </div>
          </div>
          {!reminders ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : (
            <div className="space-y-3">
              <ToggleRow k="update_reminders_enabled" label="Update reminders" hint="Nudge me when a scheduled update for the period is overdue." />
              <ToggleRow k="weekly_digest_enabled" label="Weekly investor digest" hint="Auto-send a weekly summary of updates & comments to investors." />
              <ToggleRow k="doc_reminders_enabled" label="Document reminders" hint="Nudge me about unsent drafts and unsigned documents." />
              <ToggleRow k="instant_viewer_alerts_enabled" label="Instant viewer alerts" hint="Email me the moment someone opens my deck, data room or tear sheet." />
              {reminders?.instant_viewer_alerts_enabled && (
                <div className="rounded-lg border p-3" data-testid="viewer-alert-mode-row">
                  <div className="text-sm font-medium text-foreground">Alert delivery</div>
                  <div className="text-xs text-muted-foreground">Get a ping per view, or one hourly summary when a deck goes viral.</div>
                  <div className="mt-2.5 inline-flex rounded-lg border bg-secondary/40 p-0.5">
                    {[["instant", "Instant"], ["hourly", "Hourly summary"]].map(([val, lbl]) => (
                      <button
                        key={val}
                        type="button"
                        onClick={() => setR("viewer_alert_mode", val)}
                        className={`rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${
                          (reminders?.viewer_alert_mode || "instant") === val
                            ? "bg-primary text-primary-foreground shadow-sm"
                            : "text-muted-foreground hover:text-foreground"
                        }`}
                        data-testid={`viewer-alert-mode-${val}`}
                      >
                        {lbl}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            <div className="rounded-lg border border-brand/30 bg-brand/[0.04] p-3" data-testid="founder-briefing-row">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="text-sm font-medium text-foreground">Weekly founder briefing</div>
                    <div className="text-xs text-muted-foreground">One combined weekly email - readiness, deck &amp; tear-sheet engagement, investor impact and pipeline. Replaces the separate readiness, deck and impact emails while enabled.</div>
                  </div>
                  <Switch checked={!!reminders.founder_briefing_enabled} onCheckedChange={(v) => setR("founder_briefing_enabled", v)} data-testid="founder-briefing-enabled" />
                </div>
                {reminders.founder_briefing_enabled && (
                  <div className="mt-3">
                    <Button type="button" variant="outline" size="sm" className="h-8 text-xs" onClick={sendBriefingTest} data-testid="founder-briefing-test">Send test</Button>
                  </div>
                )}
              </div>
              <div className="rounded-lg border p-3" data-testid="readiness-nudge-row">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="text-sm font-medium text-foreground">Fundraise readiness nudge</div>
                    <div className="text-xs text-muted-foreground">Weekly email when your readiness score drops or items need attention.</div>
                  </div>
                  <Switch checked={!!reminders.readiness_nudges_enabled} onCheckedChange={(v) => setR("readiness_nudges_enabled", v)} data-testid="readiness-nudge-enabled" />
                </div>
                {reminders.readiness_nudges_enabled && (
                  <div className="mt-3">
                    <Button type="button" variant="outline" size="sm" className="h-8 text-xs" onClick={sendReadinessTest} data-testid="readiness-nudge-test">Send test</Button>
                  </div>
                )}
              </div>
              <div className="rounded-lg border p-3" data-testid="deck-digest-row">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="text-sm font-medium text-foreground">Deck activity digest</div>
                    <p className="text-xs text-muted-foreground">Email me a summary of who viewed, finished or downloaded my decks.</p>
                  </div>
                  <Switch checked={!!reminders.deck_digest_enabled} onCheckedChange={(v) => setR("deck_digest_enabled", v)} data-testid="deck-digest-enabled" />
                </div>
                {reminders.deck_digest_enabled && (
                  <div className="mt-3 flex items-center gap-3">
                    <Label className="text-xs">Frequency</Label>
                    <Select value={reminders.deck_digest_frequency || "weekly"} onValueChange={(v) => setR("deck_digest_frequency", v)}>
                      <SelectTrigger className="h-8 w-32 text-xs" data-testid="deck-digest-frequency"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="daily">Daily</SelectItem>
                        <SelectItem value="weekly">Weekly</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button type="button" variant="outline" size="sm" className="h-8 text-xs" onClick={sendDeckTest} data-testid="deck-digest-test">Send test</Button>
                  </div>
                )}
              </div>
              <div className="rounded-lg border p-3" data-testid="impact-digest-row">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="text-sm font-medium text-foreground">Weekly investor-impact email</div>
                    <p className="text-xs text-muted-foreground">A rundown of your most and least helpful investors, with suggested nudges.</p>
                  </div>
                  <Switch checked={!!reminders.impact_digest_enabled} onCheckedChange={(v) => setR("impact_digest_enabled", v)} data-testid="impact-digest-enabled" />
                </div>
                {reminders.impact_digest_enabled && (
                  <div className="mt-3 flex items-center gap-3">
                    <Label className="text-xs">Frequency</Label>
                    <Select value={reminders.impact_digest_frequency || "weekly"} onValueChange={(v) => setR("impact_digest_frequency", v)}>
                      <SelectTrigger className="h-8 w-32 text-xs" data-testid="impact-digest-frequency"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="daily">Daily</SelectItem>
                        <SelectItem value="weekly">Weekly</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button type="button" variant="outline" size="sm" className="h-8 text-xs" onClick={sendImpactTest} data-testid="impact-digest-test">Send test</Button>
                  </div>
                )}
              </div>
              <div className="rounded-lg border p-3" data-testid="inbound-setup-row">
                <div className="text-sm font-medium text-foreground">Auto-log investor email replies</div>
                <p className="text-xs text-muted-foreground">Forward investor email replies to this webhook and each one is AI-scored into their impact profile automatically.</p>
                <div className="mt-2.5 space-y-1">
                  <Label className="text-[11px] text-muted-foreground">Webhook URL</Label>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 truncate rounded bg-secondary px-2 py-1.5 text-[11px] text-foreground" data-testid="inbound-webhook-url">{webhookUrl}</code>
                    <Button type="button" variant="outline" size="sm" className="h-8 gap-1 text-xs" onClick={() => copyInbound(webhookUrl, "Webhook URL")} data-testid="inbound-copy"><Copy className="h-3.5 w-3.5" /> Copy</Button>
                  </div>
                </div>
                <div className="mt-2.5 space-y-1">
                  <Label className="text-[11px] text-muted-foreground">Secret <span className="text-muted-foreground/70">(send as the <code>secret</code> field)</span></Label>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 truncate rounded bg-secondary px-2 py-1.5 text-[11px] text-foreground" data-testid="inbound-secret">
                      {inbound?.secret ? (showSecret ? inbound.secret : "•".repeat(24)) : "…"}
                    </code>
                    <Button type="button" variant="outline" size="sm" className="h-8 w-8 p-0" onClick={() => setShowSecret((v) => !v)} data-testid="inbound-secret-toggle" title={showSecret ? "Hide" : "Reveal"}>
                      {showSecret ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                    </Button>
                    <Button type="button" variant="outline" size="sm" className="h-8 gap-1 text-xs" disabled={!inbound?.secret} onClick={() => copyInbound(inbound.secret, "Secret")} data-testid="inbound-secret-copy"><Copy className="h-3.5 w-3.5" /> Copy</Button>
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <Button type="button" size="sm" className="h-8 gap-1 text-xs" disabled={inboundBusy || !inbound} onClick={testInbound} data-testid="inbound-test">
                    {inboundBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null} Test webhook
                  </Button>
                  <Button type="button" variant="outline" size="sm" className="h-8 gap-1 text-xs" disabled={inboundBusy || inbound?.env_managed} onClick={rotateSecret} data-testid="inbound-rotate">
                    <RefreshCw className="h-3.5 w-3.5" /> Rotate secret
                  </Button>
                  {inbound?.env_managed && <span className="text-[11px] text-muted-foreground">Managed via server environment</span>}
                </div>
                <p className="mt-2 text-[11px] text-muted-foreground">In your email provider's inbound routing, POST each reply as JSON <code>{`{ sender, subject, text, secret }`}</code> to the URL above. We match the sender to an investor and AI-score the message.</p>
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <Label htmlFor="draftdays">Draft unsent after (days)</Label>
                  <Input id="draftdays" type="number" min={1} max={60} value={reminders.draft_reminder_days}
                    onChange={(e) => setR("draft_reminder_days", Number(e.target.value))} className="mt-1" data-testid="draft-reminder-days" />
                </div>
                <div>
                  <Label htmlFor="sigdays">Signature reminder every (days)</Label>
                  <Input id="sigdays" type="number" min={1} max={60} value={reminders.signature_reminder_days}
                    onChange={(e) => setR("signature_reminder_days", Number(e.target.value))} className="mt-1" data-testid="signature-reminder-days" />
                </div>
                <div>
                  <Label htmlFor="sigmax">Max signature reminders</Label>
                  <Input id="sigmax" type="number" min={0} max={10} value={reminders.signature_reminder_max ?? 3}
                    onChange={(e) => setR("signature_reminder_max", Number(e.target.value))} className="mt-1" data-testid="signature-reminder-max" />
                  <p className="mt-1 text-[11px] text-muted-foreground">0 = never auto-nudge the counterparty.</p>
                </div>
              </div>
              <Button onClick={saveReminders} disabled={savingReminders} className="gap-2" data-testid="save-reminder-settings">
                {savingReminders ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save reminder settings
              </Button>
            </div>
          )}
        </Card>

        <Card className="rounded-xl border bg-card p-6" data-testid="password-card">
          <div className="mb-4 flex items-center gap-2">
            <KeyRound className="h-5 w-5 text-primary" />
            <div>
              <h2 className="h-display text-lg font-semibold text-foreground">Password</h2>
              <p className="text-xs text-muted-foreground">Change the password you use to sign in.</p>
            </div>
          </div>
          <form onSubmit={changePassword} className="space-y-4">
            <div>
              <Label htmlFor="curpw">Current password</Label>
              <Input id="curpw" type="password" value={pw.current_password} onChange={(e) => setPw((p) => ({ ...p, current_password: e.target.value }))} required className="mt-1" data-testid="current-password" />
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="newpw">New password</Label>
                <Input id="newpw" type="password" value={pw.new_password} onChange={(e) => setPw((p) => ({ ...p, new_password: e.target.value }))} required minLength={8} placeholder="At least 8 characters" className="mt-1" data-testid="new-password" />
              </div>
              <div>
                <Label htmlFor="confpw">Confirm new password</Label>
                <Input id="confpw" type="password" value={pw.confirm} onChange={(e) => setPw((p) => ({ ...p, confirm: e.target.value }))} required minLength={8} className="mt-1" data-testid="confirm-password" />
              </div>
            </div>
            <Button type="submit" disabled={pwSaving} className="gap-2" data-testid="change-password-submit">
              {pwSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />} Update password
            </Button>
          </form>
        </Card>

        <TrustedDevicesCard />

        {company && <AuditTrailCard company={company} />}
      </div>
    </AppShell>
  );
}
